import { useState } from "react"
import { useQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import { useViewer } from "~/auth/use-viewer"
import { plansPath } from "~/common/paths" // Use admin path for creation/viewing
import { TablePageLayout } from "~/layouts/table-page-layout"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { Skeleton } from "~/ui/skeleton"
import { NEARBY_PLANS_QUERY } from "./nearby-plans-query" // Import the query
import { JoinPlanModal } from "./join-plan-modal" // Import the modal

export const UserPlansScreen = () => {
  const { viewer } = useViewer()
  const [distance, setDistance] = useState(25) // Default distance (e.g., 25 miles)
  const [joinPlanId, setJoinPlanId] = useState<string | null>(null) // State for modal

  // Initialize 'now' only once on component mount
  const [now] = useState(() => new Date().toISOString())

  const {
    data: plansData,
    loading: plansLoading,
    error: plansError,
  } = useQuery(NEARBY_PLANS_QUERY, {
    variables: {
      // Provide default 0 only to satisfy TS when skipped; actual values used when query runs.
      latitude: viewer?.latitude ?? 0,
      longitude: viewer?.longitude ?? 0,
      distance: distance,
      fromDatetime: now, // Fetch plans from now onwards
    },
    skip: !viewer?.latitude || !viewer?.longitude, // Skip if no location
    fetchPolicy: "cache-and-network", // Ensure fresh data when navigating back/forth
  })

  const plans = plansData?.nearbyPlans?.edges?.map((edge) => edge?.node) ?? []

  // Find the plan title for the modal
  const joinPlanTitle = plans.find((plan) => plan?.id === joinPlanId)?.title ?? ""
  // Determine if the current viewer has joined the plan selected for the modal
  const isJoined =
    plans
      .find((plan) => plan?.id === joinPlanId)
      ?.planParticipations.edges.some((edge) => edge?.node?.user.id === viewer?.id) ?? false

  return (
    <TablePageLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl">Plans Near Me</h1>
          <p className="text-sm text-gray-500">
            Showing plans within {distance} miles of your location
          </p>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="distance" className="text-sm">
              Distance:
            </label>
            <select
              id="distance"
              className="rounded border border-gray-300 px-2 py-1"
              value={distance}
              onChange={(e) => setDistance(Number(e.target.value))}
            >
              <option value={5}>5 miles</option>
              <option value={10}>10 miles</option>
              <option value={25}>25 miles</option>
              <option value={50}>50 miles</option>
              <option value={100}>100 miles</option>
            </select>
          </div>
          {/* Link to the user plan creation screen */}
          <Link to={`${plansPath({})}/new`}>
            <Button variant="outline">Make a Plan</Button>
          </Link>
        </div>
      </div>
      <hr className="my-4 border-0 border-b" />

      {!viewer?.latitude || !viewer?.longitude ? (
        <div className="rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Location Needed</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Please update your profile with your location to see nearby plans.</p>
              </div>
            </div>
          </div>
        </div>
      ) : plansLoading ? (
        // Show skeleton loaders while plans are loading
        <div className="space-y-4">
          {[...Array(3)].map(
            (
              _,
              i // Render 3 skeleton placeholders
            ) => (
              <div key={i} className="rounded-lg border p-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Skeleton for Plan Details */}
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-3/4" /> {/* Title */}
                    <Skeleton className="h-4 w-1/2" /> {/* Location */}
                    <Skeleton className="h-4 w-1/2" /> {/* Datetime */}
                    <Skeleton className="h-4 w-full" /> {/* Description line 1 */}
                    <Skeleton className="h-4 w-5/6" /> {/* Description line 2 */}
                  </div>
                  {/* Skeleton for Action Buttons */}
                  <div className="flex flex-shrink-0 gap-2">
                    <Skeleton className="h-8 w-16" /> {/* Join/Leave Button */}
                    <Skeleton className="h-8 w-16" /> {/* View Button */}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      ) : plansError ? (
        <GraphqlError error={plansError} />
      ) : plans.length === 0 ? (
        <div>No upcoming plans found within {distance} miles.</div>
      ) : (
        <div className="space-y-4">
          {plans.map(
            (plan) =>
              plan && (
                <div key={plan.id} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Plan Details */}
                    <div>
                      <h3 className="font-semibold">{plan.title}</h3>
                      <p className="text-sm text-gray-600">
                        {plan.location.name} ({plan.distance?.toFixed(1)} miles away)
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(plan.datetime).toLocaleString()}
                      </p>
                      {plan.description && <p className="mt-1 text-sm">{plan.description}</p>}
                    </div>
                    {/* Action Buttons */}
                    <div className="flex flex-shrink-0 gap-2">
                      {plan.planParticipations.edges.some(
                        (edge) => edge?.node?.user.id === viewer?.id
                      ) ? (
                        // User has joined -> Show Leave button
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setJoinPlanId(plan.id)}
                        >
                          Leave
                        </Button>
                      ) : (
                        // User has not joined -> Show Join button
                        <Button variant="default" size="sm" onClick={() => setJoinPlanId(plan.id)}>
                          Join
                        </Button>
                      )}
                      {/* Link to user plan details */}
                      <Link to={`${plansPath({})}/${plan.id}`}>
                        <Button variant="outline" size="sm">
                          View
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )
          )}
        </div>
      )}

      {/* Join/Leave Plan Modal */}
      {joinPlanId && (
        <JoinPlanModal
          planId={joinPlanId}
          planTitle={joinPlanTitle}
          isOpen={!!joinPlanId}
          onClose={() => setJoinPlanId(null)}
          isJoined={isJoined} // Pass whether the user is currently joined
        />
      )}
    </TablePageLayout>
  )
}
